/**
 * Petits utilitaires d’affichage propres à la campagne.
 *
 * Aucun texte d’interface n’est écrit deux fois : les libellés d’enum viennent
 * de `@tb/shared` (`LIBELLES_*`), ce fichier ne fait que les composer.
 */

import {
  comparerSieges,
  LIBELLES_NATURE_FORMULE,
  type FormuleCampagne,
  type PhaseSaison,
  type SiegeProposition,
  type StatutProposition,
} from '@tb/shared'

import { ABSENT } from '../../lib/format'
import { CHEMINS } from '../../lib/chemins'

/* -------------------------------------------------------------------------- */
/* Formules et sièges                                                         */
/* -------------------------------------------------------------------------- */

/** « Série imposée · Parterre » — la nature, et la zone quand elle contraint. */
export function precisionFormule(formule: FormuleCampagne): string {
  const nature = LIBELLES_NATURE_FORMULE[formule.nature]
  return formule.zoneNom === null ? nature : `${nature} · ${formule.zoneNom}`
}

/** Sièges triés naturellement puis joints : `« F12, F13 »`. */
export function listerSieges(sieges: readonly SiegeProposition[]): string {
  if (sieges.length === 0) return ABSENT
  return [...sieges]
    .sort(comparerSieges)
    .map((siege) => siege.libelle)
    .join(', ')
}

/* -------------------------------------------------------------------------- */
/* Étapes de la campagne                                                      */
/* -------------------------------------------------------------------------- */

export interface EtapeCampagne {
  readonly id: string
  /** Segment de route, relatif à `/admin/campagne`. `''` = l’index. */
  readonly segment: string
  readonly numero: number | null
  readonly libelle: string
  readonly description: string
}

/**
 * Le déroulé de la campagne, dans l’ordre où Anne le vit (SPEC-FONCTIONNELLE
 * §6). Le tableau de bord n’est pas une étape : c’est le point de retour.
 */
export const ETAPES_CAMPAGNE: readonly EtapeCampagne[] = [
  {
    id: 'suivi',
    segment: '',
    numero: null,
    libelle: 'Tableau de bord',
    description: 'Où en est la campagne',
  },
  {
    id: 'correspondances',
    segment: 'correspondances',
    numero: 1,
    libelle: 'Correspondance des formules',
    description: 'Associer chaque formule de la saison passée à une formule de la nouvelle',
  },
  {
    id: 'generation',
    segment: 'generation',
    numero: 2,
    libelle: 'Génération des propositions',
    description: 'Créer une proposition par abonné de la saison passée',
  },
  {
    id: 'bulletins',
    segment: 'bulletins',
    numero: 3,
    libelle: 'Traitement des bulletins',
    description: 'Accepter, replacer ou décliner les réponses reçues',
  },
  {
    id: 'phases',
    segment: 'phases',
    numero: 4,
    libelle: 'Ouverture des phases',
    description: 'Ouvrir la phase interne, puis la phase externe',
  },
]

/** URL absolue d’une étape, saison comprise. */
export function urlEtape(segment: string, saisonId: string | null): string {
  const base = segment === '' ? CHEMINS.campagne : `${CHEMINS.campagne}/${segment}`
  return saisonId === null ? base : `${base}?saison=${encodeURIComponent(saisonId)}`
}

/* -------------------------------------------------------------------------- */
/* Statuts                                                                    */
/* -------------------------------------------------------------------------- */

/** Ce que chaque statut veut dire pour le personnel, en une phrase. */
export const EXPLICATIONS_STATUT: Readonly<Record<StatutProposition, string>> = {
  en_attente: 'Bulletin non encore reçu — les sièges restent gelés.',
  acceptee: 'Souscription créée pour la nouvelle saison.',
  declinee: 'L’abonné ne se réabonne pas — ses sièges sont libérés.',
  expiree: 'Sans réponse à l’ouverture de la phase externe — sièges libérés.',
}

/** Ce que la phase autorise, dit simplement. */
export const EXPLICATIONS_PHASE: Readonly<Record<PhaseSaison, string>> = {
  preparation:
    'La saison se monte : spectacles, tarifs et formules. Aucune proposition n’est encore active.',
  interne:
    'Les abonnés fidèles sont prioritaires : leurs sièges sont gelés et la vente au public est bloquée.',
  externe: 'Les propositions restantes ont expiré. La vente au public est ouverte.',
  cloturee: 'La saison est terminée. Elle ne sert plus que d’historique.',
}

/* -------------------------------------------------------------------------- */
/* Prérequis manquants (CAM-02)                                               */
/* -------------------------------------------------------------------------- */

export interface LienEcran {
  readonly libelle: string
  readonly to: string
}

/**
 * Un manque annoncé par le backend (`details.manques`) est du texte destiné à
 * un humain. On lui adjoint le lien vers l’écran qui permet de le corriger,
 * repéré sur des mots-clés — jamais deviné sur autre chose.
 */
export function ecranPourManque(manque: string): LienEcran | null {
  const texte = manque.toLowerCase()
  if (texte.includes('grille') || texte.includes('tarif')) {
    return { libelle: 'Ouvrir la tarification', to: CHEMINS.tarification }
  }
  if (texte.includes('formule')) {
    return { libelle: 'Ouvrir les formules', to: CHEMINS.formules }
  }
  if (texte.includes('spectacle') || texte.includes('représentation')) {
    return { libelle: 'Ouvrir le catalogue', to: CHEMINS.catalogue }
  }
  if (texte.includes('salle') || texte.includes('zone') || texte.includes('catégorie')) {
    return { libelle: 'Ouvrir la configuration', to: CHEMINS.configuration }
  }
  return null
}

/**
 * Extrait la liste des manques d’une erreur `PHASE_INVALIDE`.
 * Le backend la fournit dans `details.manques` ; à défaut, l’écran se rabat
 * sur le message métier complet, qui les énumère déjà.
 */
export function manquesDeDetails(details: Record<string, unknown> | undefined): string[] {
  const manques = details?.manques
  if (!Array.isArray(manques)) return []
  return manques.filter((manque): manque is string => typeof manque === 'string')
}
